import { useCallback, useState } from "react"
import { lumi } from "@/lib/lumi"

export type CalculatorType = "fat" | "tdee" | "water"

export interface HistoryRecord {
  _id: string
  calculatorType: CalculatorType
  userId: string
  summary: string
  details: string
  gender?: string
  age?: number
  goal?: string
  activity?: string
  creator?: string
  createdAt: string
  updatedAt: string
}

export interface HistoryPayload {
  calculatorType: CalculatorType
  summary: string
  details: Record<string, unknown>
  gender?: string
  age?: number
  goal?: string
  activity?: string
}

export function useCalculationHistory() {
  const [records, setRecords] = useState<HistoryRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const fetchHistory = useCallback(async (userId: string, calculatorType?: CalculatorType) => {
    if (!userId) return
    setLoading(true)
    setError("")
    try {
      const filter: Record<string, string> = { userId }
      if (calculatorType) filter.calculatorType = calculatorType
      const { list } = await lumi.entities.calculation_history.list({
        filter,
        sort: { createdAt: -1 },
      })
      setRecords((list || []) as HistoryRecord[])
    } catch (err) {
      console.error("Failed to load calculation history:", err)
      setError("Could not load your saved calculations. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [])

  const saveHistory = useCallback(async (userId: string, payload: HistoryPayload) => {
    if (!userId) {
      throw new Error("Please sign in to save your calculation.")
    }
    setSaving(true)
    setError("")
    try {
      const now = new Date().toISOString()
      const created = await lumi.entities.calculation_history.create({
        calculatorType: payload.calculatorType,
        userId,
        summary: payload.summary,
        details: JSON.stringify(payload.details),
        gender: payload.gender || "",
        age: payload.age ?? 0,
        goal: payload.goal || "",
        activity: payload.activity || "",
        creator: userId,
        createdAt: now,
        updatedAt: now,
      })
      setRecords((prev) => [created as HistoryRecord, ...prev])
      return created as HistoryRecord
    } catch (err) {
      console.error("Failed to save calculation history:", err)
      setError("Could not save this calculation. Please try again.")
      throw err
    } finally {
      setSaving(false)
    }
  }, [])

  const deleteHistory = useCallback(async (id: string) => {
    try {
      await lumi.entities.calculation_history.delete(id)
      setRecords((prev) => prev.filter((item) => item._id !== id))
    } catch (err) {
      console.error("Failed to delete calculation history:", err)
      setError("Could not delete this calculation. Please try again.")
      throw err
    }
  }, [])

  return {
    records,
    loading,
    saving,
    error,
    fetchHistory,
    saveHistory,
    deleteHistory,
  }
}
